import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Kicker from "components/TextElements/Kicker"
import Button from "components/buttons/Button"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { fresponsive } from "utils/fullyResponsive"
import { returnContactUsLink } from "utils/routes"
import useMedia from "utils/useMedia"

export default function CTA() {
  const images: Queries.CTAQuery = useStaticQuery(graphql`
    query CTA {
      mainPic: file(relativePath: { eq: "global/CTA2.webp" }) {
        ...LocalImageQuery
      }
      mainPicM: file(relativePath: { eq: "global/CTA2M.webp" }) {
        ...LocalImageQuery
      }
    }
  `)

  const [MainPicPNG, MainPicM] = [
    images.mainPic?.childImageSharp?.gatsbyImageData,
    images.mainPicM?.childImageSharp?.gatsbyImageData,
  ]

  const { t, language } = useI18next()
  const contactLink = returnContactUsLink( language );
  const mainPic = useMedia(MainPicPNG, MainPicPNG, MainPicPNG, MainPicM)

  const startLines = [
    t("Contact us and tell us a little about your needs."),
    t("Talk with our sales team and get a custom quote."),
    t("Start building new products, features, and financial tools."),
  ]

  const StartLines = startLines.map((line, lineIndex) => {
    return (
      <StartLine key={line}>
        <StartNumber>{lineIndex + 1}</StartNumber>
        <TheLineItself>{line}</TheLineItself>
      </StartLine>
    )
  })

  return (
    <Wrapper>
      <InnerWrapper setWidth={useMedia("1660px", "100%", "100%", "100%")}>
        <InnerInnerWrapper>
          <Kicker
            kickerText={t("CUSTOM PRICING") ?? "CUSTOM PRICING"}
            kickerIcon="discs2"
            color={colors.seaFoam300}
          />
          <Wrap>
            <Title>{t("Priced just for you.")}</Title>

            <BodyButton>
              <Body>
                {t(
                  "Galileo offers endless products, features and services. Choose the ones that best suit your customers’ needs to create your own unique digital banking program. Be your own program manager—taking on as few or as many functions as you like—or outsource your program management to us. Your pricing depends on your choices, and our experienced sales team will work with you to create your own custom pricing proposal."
                )}
              </Body>
              <Button styledLink href={contactLink} lightMode secondary arrow>
                {t("Let's Talk")}
              </Button>
            </BodyButton>
          </Wrap>
          <BottomSection>
            <StartBox>
              <StartTitle>
                {t("Start building the future of fintech today.")}
              </StartTitle>
              <NumberedLines>{StartLines}</NumberedLines>
              <Button styledLink href={contactLink} lightMode arrow>
                {t("Let's Talk")}
              </Button>
            </StartBox>
            {mainPic && (
              <MainPicWrapper>
                <MainPic image={mainPic} alt={t("dashboard ui and card")} />
              </MainPicWrapper>
            )}
          </BottomSection>
        </InnerInnerWrapper>
      </InnerWrapper>
    </Wrapper>
  )
}

const StartLine = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.blue200};
  ${text.bodyR}
  ${media.mobile} {
    ${text.bodyS}
  }
`
const StartNumber = styled.div`
  ${text.captionKicker}
  color: ${colors.white};
  background-color: ${colors.seaFoam500};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${media.fullWidth} {
    width: 24px;
    height: 24px;
    margin-right: 10px;
    padding-left: 2px;
  }
  ${media.desktop} {
    width: 1.446vw;
    height: 1.446vw;
    margin-right: 0.602vw;
    padding-left: 0.12vw;
  }
  ${media.tablet} {
    width: 2.344vw;
    height: 2.344vw;
    margin-right: 0.977vw;
    padding-left: 0.195vw;
  }
  ${media.mobile} {
    width: 6.4vw;
    height: 6.4vw;
    margin-right: 2.667vw;
    padding-left: 0.533vw;
  }

  border-radius: 50%;
`
const TheLineItself = styled.div``

const Wrapper = styled.div`
  overflow: hidden;

  ${media.fullWidth} {
    padding-top: 150px;
    padding-bottom: 500px;
  }
  ${media.desktop} {
    padding-top: 9.036vw;
    padding-bottom: 30.12vw;
  }
  ${media.tablet} {
    padding-top: 14.648vw;
    padding-bottom: 28vw;
  }
  ${media.mobile} {
    padding: 21.333vw 0 85vw 5.333vw;
  }

  width: 100%;
  display: flex;
  justify-content: center;
  max-width: 100vw;
  overflow-x: hidden;
`
const InnerInnerWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  ${media.fullWidth} {
    padding-left: 127px;
  }
  ${media.desktop} {
    padding-left: 7.651vw;
  }
  ${media.tablet} {
    padding-left: 9.277vw;
  }
`
const Wrap = styled.div`
  display: flex;

  ${fresponsive(css`
    gap: 75px;
  `)}
  ${media.tablet} {
    gap: 2.667vw;
    flex-direction: column;
  }

  ${media.mobile} {
    gap: 2.667vw;
    flex-direction: column;
  }
`

const Title = styled.div`
  ${text.h2}
  color: ${colors.blue800};
  ${media.fullWidth} {
    width: 573px;
  }
  ${media.desktop} {
    width: 34.518vw;
  }
  ${media.tablet} {
    ${text.h2}
  }
  ${media.mobile} {
    ${text.h1Mobile}
    margin-bottom: 2.667vw;
  }
`
const BodyButton = styled.div`
  position: relative;
  ${media.fullWidth} {
    width: 582px;
  }
  ${media.desktop} {
    width: 35.06vw;
  }
  ${media.tablet} {
    margin-top: 1.953vw;
    width: 81.445vw;
  }
  ${media.mobile} {
    padding-right: 5.333vw;
  }
`

const Body = styled.div`
  ${text.bodyR}
  color: ${colors.blue200};
  ${media.fullWidth} {
    margin-bottom: 40px;
  }
  ${media.desktop} {
    margin-bottom: 2.41vw;
  }
  ${media.tablet} {
    margin-bottom: 3.906vw;
  }
  ${media.mobile} {
    margin-bottom: 5.333vw;
  }
`

const BottomSection = styled.div`
  display: flex;
  justify-content: flex-start;

  ${media.fullWidth} {
    height: 741px;
    margin-top: 150px;
  }
  ${media.desktop} {
    height: 44.639vw;
    margin-top: 9.036vw;
  }
  ${media.tablet} {
    margin-top: 9.766vw;
  }
  ${media.mobile} {
    flex-direction: column;
    margin-top: 21.333vw;
  }
`

const StartBox = styled.div`
  ${media.fullWidth} {
    margin-top: 150px;
  }
  ${media.desktop} {
    margin-top: 9.036vw;
  }
  ${media.tablet} {
    width: 45.41vw;
  }
  ${media.mobile} {
    padding-right: 5.333vw;
  }
`

const StartTitle = styled.div`
  ${text.h4}
  background: ${colors.turqBlueHorz};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  ${media.fullWidth} {
    width: 626px;
    margin-bottom: 20px;
  }
  ${media.desktop} {
    width: 37.711vw;
    margin-bottom: 1.205vw;
  }
  ${media.tablet} {
    ${text.h5}
    width: 34.082vw;
    margin-bottom: 1.953vw;
  }
  ${media.mobile} {
    ${text.h5}
    margin-bottom: 5.333vw;
  }
`

const NumberedLines = styled.div`
  display: flex;
  flex-direction: column;

  ${media.fullWidth} {
    row-gap: 10px;
    margin-bottom: 40.01px;
  }
  ${media.desktop} {
    row-gap: 0.602vw;
    margin-bottom: 2.41vw;
  }
  ${media.tablet} {
    row-gap: 0.977vw;
    margin-bottom: 3.906vw;
  }
  ${media.mobile} {
    row-gap: 4vw;
    margin-bottom: 10.667vw;
  }
`

const MainPicWrapper = styled.div`
  position: absolute;

  ${media.fullWidth} {
    width: 1006px;
    height: 741px;
    right: -122px;
    bottom: 0;
  }
  ${media.desktop} {
    width: 60.602vw;
    height: 44.639vw;
    right: -7.349vw;
    bottom: 0;
  }
  ${media.tablet} {
    width: 72.803vw;
    height: 53.394vw;
    right: -28.349vw;
  }
  ${media.mobile} {
    position: relative;
    width: 150.933vw;
    max-width: unset;
    height: 109.333vw;
    margin-top: 16vw;
    right: auto;
  }
`

const MainPic = styled(GatsbyImage)`
  position: absolute;
`
